import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChartLine, Lock, Sparkle } from "@phosphor-icons/react";
import api from "@/lib/api";
import {
  BarChart, Bar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";

const MACRO_COLORS = ["#4169E1", "#FFEA00", "#A1A5B5"];

export default function Analytics() {
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/analytics/advanced")
      .then(({ data }) => { setData(data); setLocked(false); })
      .catch((e) => { if (e?.response?.status === 403) setLocked(true); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-10 label-tiny text-text-secondary">Loading...</div>;

  if (locked) {
    return (
      <div className="p-6 lg:p-10 max-w-3xl mx-auto" data-testid="analytics-locked">
        <div className="mb-8">
          <div className="label-tiny text-brand-accent mb-2">// DEEP INSIGHTS</div>
          <h1 className="font-heading text-4xl sm:text-5xl">ADVANCED ANALYTICS</h1>
        </div>
        <div className="tactical-card p-12 text-center stripe-bg relative overflow-hidden">
          <div className="relative z-10">
            <Lock size={48} weight="duotone" className="text-brand-accent mx-auto mb-4" />
            <h2 className="font-heading text-2xl mb-3">PRO FEATURE</h2>
            <p className="text-sm text-text-secondary mb-6 max-w-md mx-auto">
              Unlock muscle volume analysis, macro split tracking, recovery-performance correlation, and 12-week training trends with Edge Training Pro.
            </p>
            <Link to="/upgrade" data-testid="analytics-upgrade-link">
              <button className="btn-kinetic px-6 py-3 text-xs label-tiny tracking-widest inline-flex items-center gap-2">
                <Sparkle size={14} weight="fill" /> UNLOCK PRO
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const macroData = [
    { name: "Protein", value: data.macro_split.protein_pct },
    { name: "Carbs", value: data.macro_split.carbs_pct },
    { name: "Fat", value: data.macro_split.fat_pct },
  ];

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="analytics-page">
      <div className="mb-8">
        <div className="label-tiny text-brand-accent mb-2">// PRO INTEL</div>
        <h1 className="font-heading text-4xl sm:text-5xl">ADVANCED ANALYTICS</h1>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-3 gap-px bg-white/10 mb-6">
        <div className="bg-surface-DEFAULT p-5" data-testid="lifetime-workouts">
          <div className="label-tiny mb-2">LIFETIME WORKOUTS</div>
          <div className="font-heading text-3xl tabular-nums">{data.totals.lifetime_workouts}</div>
        </div>
        <div className="bg-surface-DEFAULT p-5" data-testid="lifetime-duration">
          <div className="label-tiny mb-2">TOTAL MINUTES</div>
          <div className="font-heading text-3xl tabular-nums text-brand-accent">{data.totals.lifetime_duration_min.toLocaleString()}</div>
        </div>
        <div className="bg-surface-DEFAULT p-5" data-testid="lifetime-meals">
          <div className="label-tiny mb-2">MEALS LOGGED</div>
          <div className="font-heading text-3xl tabular-nums">{data.totals.lifetime_meals_logged}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="tactical-card p-6" data-testid="muscle-volume-chart">
          <h3 className="font-heading text-lg mb-4">MUSCLE VOLUME (LBS × SETS)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={data.muscle_volume}>
              <PolarGrid stroke="rgba(255,255,255,0.1)" />
              <PolarAngleAxis dataKey="muscle" stroke="#A1A5B5" fontSize={11} />
              <PolarRadiusAxis stroke="rgba(255,255,255,0.1)" fontSize={10} />
              <Radar dataKey="volume" stroke="#FFEA00" fill="#FFEA00" fillOpacity={0.3} />
              <Tooltip contentStyle={{ background: "#12141A", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <div className="tactical-card p-6" data-testid="macro-pie-chart">
          <h3 className="font-heading text-lg mb-4">MACRO SPLIT (30 DAYS)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={macroData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={(entry) => `${entry.name}: ${entry.value}%`}>
                {macroData.map((_, i) => <Cell key={i} fill={MACRO_COLORS[i]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#12141A", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="tactical-card p-6 mb-6" data-testid="volume-trend-chart">
        <h3 className="font-heading text-lg mb-4">12-WEEK TRAINING VOLUME (MIN/WEEK)</h3>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data.volume_trend}>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="week" stroke="#A1A5B5" fontSize={10} />
            <YAxis stroke="#A1A5B5" fontSize={11} />
            <Tooltip contentStyle={{ background: "#12141A", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} cursor={{ fill: "rgba(255,234,0,0.06)" }} />
            <Bar dataKey="minutes" fill="#4169E1" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {data.recovery_performance.length > 0 && (
        <div className="tactical-card p-6" data-testid="recovery-performance-chart">
          <h3 className="font-heading text-lg mb-4">RECOVERY × TRAINING DURATION</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.recovery_performance}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" stroke="#A1A5B5" fontSize={10} />
              <YAxis stroke="#A1A5B5" fontSize={11} />
              <Tooltip contentStyle={{ background: "#12141A", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
              <Line type="monotone" dataKey="recovery" stroke="#FFEA00" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="duration" stroke="#4169E1" strokeWidth={2} dot={{ r: 3 }} />
              <Legend wrapperStyle={{ fontSize: "11px" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
